import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getLoading } from "../../Application/Selectors/loader";
import { loadCustomerList } from "../../Application/Actions/loader";
import { getAllCustomerList } from "../../Application/Selectors/customer";
import { db } from "./firebase";
import {
  collection,
  onSnapshot,
  doc,
  updateDoc,
  arrayUnion,
} from "firebase/firestore";

type Customer = {
  id: string;
  name?: string;
};

type ChatItem = {
  Sender: string;
  Message: string;
  CreatedOn: { seconds: number; nanoseconds: number } | Date;
};

type Ticket = {
  tenantId: string;
  id: string;
  TicketId?: string;
  Issue?: string;
  Category?: string;
  Status?: string;
  AssignedToName?: string | null;
  Chat?: ChatItem[];
  CreatedOn?: { seconds: number; nanoseconds: number };
  [key: string]: any;
};

function Ticket() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [allTickets, setAllTickets] = useState<Ticket[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    null
  );
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [chatMessage, setChatMessage] = useState("");

  useEffect(() => {
    dispatch(loadCustomerList);
  }, []);

  const customerList: Customer[] = useSelector(getAllCustomerList);
  const loading: boolean = useSelector(getLoading).loading;

  useEffect(() => {
    if (!customerList || customerList.length === 0) return;

    const unsubscribes: (() => void)[] = [];

    customerList.forEach((customer: Customer) => {
      if (!customer.id) return;

      const ticketsRef = collection(db, customer.id);

      const unsubscribe = onSnapshot(
        ticketsRef,
        (querySnapshot) => {
          const tickets: Ticket[] = [];
          querySnapshot.forEach((docSnap) => {
            tickets.push({
              tenantId: customer.id,
              id: docSnap.id,
              ...docSnap.data(),
            });
          });

          setAllTickets((prev) => {
            const others = prev.filter((t) => t.tenantId !== customer.id);
            return [...others, ...tickets];
          });
        },
        (error) => {
          console.error(`Error fetching tickets for ${customer.id}:`, error);
        }
      );

      unsubscribes.push(unsubscribe);
    });

    return () => unsubscribes.forEach((u) => u());
  }, [customerList]);

  // Group tickets by tenantId
  const groupedTickets = allTickets.reduce<Record<string, Ticket[]>>(
    (acc, ticket) => {
      if (!acc[ticket.tenantId]) acc[ticket.tenantId] = [];
      acc[ticket.tenantId].push(ticket);
      return acc;
    },
    {}
  );

  // Format Firestore timestamp
  const formatDate = (
    ts?: { seconds: number; nanoseconds: number } | Date
  ) => {
    if (!ts) return "-";
    if (ts instanceof Date) {
      return ts.toLocaleString();
    }
    const date = new Date(ts.seconds * 1000 + ts.nanoseconds / 1000000);
    return date.toLocaleString();
  };

  // Send chat message
  const handleSendMessage = async () => {
    if (!chatMessage.trim()) return;
    if (!selectedCustomer?.id || !selectedTicket?.id) return;

    const newMessage: ChatItem = {
      Sender: "Support",
      Message: chatMessage,
      CreatedOn: new Date(),
    };

    try {
      const ticketDocRef = doc(db, selectedCustomer.id, selectedTicket.id);
      await updateDoc(ticketDocRef, {
        Chat: arrayUnion(newMessage),
      });
      setChatMessage("");
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };
// add another effect just for selectedTicket
useEffect(() => {
  if (!selectedCustomer?.id || !selectedTicket?.id) return;

  const ticketDocRef = doc(db, selectedCustomer.id, selectedTicket.id);

  const unsubscribe = onSnapshot(ticketDocRef, (docSnap) => {
    if (docSnap.exists()) {
      setSelectedTicket({
        tenantId: selectedCustomer.id,
        id: docSnap.id,
        ...docSnap.data(),
      } as Ticket);
    }
  });

  return () => unsubscribe();
}, [selectedCustomer, selectedTicket?.id]);

  return (
    <div className="tickets-container p-6">
      <h3 className="text-2xl font-bold mb-4">All Customers</h3>

      {/* Customer List */}
      <table className="min-w-full border mb-6">
        <thead>
          <tr className="bg-gray-100">
            <th className="border px-4 py-2 text-left">Customer</th>
            <th className="border px-4 py-2 text-left">Ticket Counts</th>
            <th className="border px-4 py-2 text-left">Action</th>
          </tr>
        </thead>
        <tbody>
            {customerList
            .filter((customer) => groupedTickets[customer.id]) // ✅ only show customers that have tickets
            .map((customer) => (
              <tr key={customer.id} className="hover:bg-gray-50">
                <td className="px-4 py-2 border-b">{customer.name || "-"}</td>
                <td className="px-4 py-2 border-b">
                  {groupedTickets[customer.id]?.length || 0}
                </td>
                <td className="px-4 py-2 border-b">
                  <button
                   onClick={() => {
                    setSelectedCustomer(customer);
                    setSelectedTicket(null);
                  }}
                    className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    View Tickets
                  </button>
                </td>
              </tr>
            ))}
         
        </tbody>
      </table>

      {/* Tickets for Selected Customer */}
      {selectedCustomer && (
        <div className="mt-6">
          <h4 className="text-xl font-semibold mb-2">
            Tickets for {selectedCustomer.name || selectedCustomer.id}
          </h4>

          {groupedTickets[selectedCustomer.id]?.map((ticket) => (
            <div
              key={ticket.id}
              className="border rounded-lg p-4 mb-3 bg-gray-50 shadow-sm"
            >
              <h5 className="font-bold">{ticket.Issue || "No Issue Title"}</h5>
              <p>
                <strong>Ticket No:</strong> {ticket.TicketId || "-"}
              </p>
              <p>
                <strong>Category:</strong> {ticket.Category || "-"}
              </p>
              <p>
                <strong>Status:</strong> {ticket.Status || "-"}
              </p>
              <p>
                <strong>Created On:</strong> {formatDate(ticket.CreatedOn)}
              </p>

              <button
                onClick={() => setSelectedTicket(ticket)}
                className="mt-2 text-blue-500 underline"
              >
                View Chat
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Chat Box */}
      {selectedTicket && selectedCustomer && (
        <div className="chat-box border rounded-lg p-4 mt-4 bg-white shadow-md">
          <h3 >
            <strong>           {selectedTicket.TicketId }
</strong>
          </h3>
          <h4 className="font-semibold mb-3">
            Chat : {selectedTicket.Issue || selectedTicket.id}
          </h4>

          {/* Messages */}
          <div className="chat-messages max-h-64 overflow-y-auto mb-1 p-2 border">
            {selectedTicket.Chat && selectedTicket.Chat.length > 0 ? (
              selectedTicket.Chat.map((c, idx) => (
                <div
                  key={idx}
                  className={`p-2 my-1 rounded-md ${
                    c.Sender === "Support"
                      ? "bg-green-100 text-right"
                      : "bg-blue-200 text-left"
                  }`}
                >
                  <p className="text-sm">
                    <strong>{c.Sender}:</strong> {c.Message}
                  </p>
                  <span className="text-xs text-red-500">
                    {formatDate(c.CreatedOn)}
                  </span>
                </div>
              ))
            ) : (
              <p className="text-gray-500">No messages yet.</p>
            )}
          </div>

          {/* Input */}
          <div className="chat-input flex">
            <input
              type="text"
              value={chatMessage}
              onChange={(e) => setChatMessage(e.target.value)}
              placeholder="Type your message..."
              className="flex-1 border rounded-l-lg px-3 py-2 focus:outline-none"
            />
            <button
              onClick={handleSendMessage}
              className="bg-blue-500 text-white px-4 py-2 rounded-r-lg hover:bg-blue-600"
            >
              Send
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Ticket;
